/**
 * TimestampDisplay Component
 * Displays relative time with absolute time on hover
 * Used for showing createdAt/updatedAt timestamps
 */

import { Tooltip, Typography } from 'antd';
import { CalendarOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime, formatAbsoluteTime } from '@/utils/dateUtils';

const { Text } = Typography;

interface TimestampDisplayProps {
  /** ISO date string, Date object, or timestamp */
  date: string | Date | number | null | undefined;
  /** Label to show before the timestamp (e.g., "Created", "Updated") */
  label?: string;
  /** Whether to show the label inline */
  showLabel?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Text color - defaults to secondary (dimmed) */
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  /** Icon to display before the timestamp */
  icon?: React.ReactNode;
}

/**
 * Displays a timestamp with relative time (e.g., "2 hours ago")
 * and absolute time on hover (e.g., "2025-12-04 14:30:00")
 */
export function TimestampDisplay({
  date,
  label,
  showLabel = true,
  className,
  type = 'secondary',
  icon,
}: TimestampDisplayProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  if (!date) {
    return null;
  }

  const relativeTime = formatRelativeTime(date, locale);
  const absoluteTime = formatAbsoluteTime(date, locale);

  return (
    <Tooltip title={absoluteTime} placement="top">
      <Text type={type} className={className} style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        {icon}
        {showLabel && label && <span>{label}: </span>}
        {relativeTime}
      </Text>
    </Tooltip>
  );
}

interface TimestampPairProps {
  /** Created timestamp */
  createdAt?: string | Date | number | null;
  /** Updated timestamp */
  updatedAt?: string | Date | number | null;
  /** Custom className for styling */
  className?: string;
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  /** Show icons next to timestamps */
  showIcons?: boolean;
}

/**
 * Displays both createdAt and updatedAt timestamps
 */
export function TimestampPair({
  createdAt,
  updatedAt,
  className,
  direction = 'horizontal',
  showIcons = true,
}: TimestampPairProps) {
  const { t } = useTranslation();

  if (!createdAt && !updatedAt) {
    return null;
  }

  const containerStyle =
    direction === 'horizontal'
      ? 'tw-flex tw-items-center tw-gap-4'
      : 'tw-flex tw-flex-col tw-gap-1';

  return (
    <div className={`${containerStyle} ${className ?? ''}`}>
      {createdAt && (
        <TimestampDisplay
          date={createdAt}
          label={t('createdAt')}
          showLabel={true}
          icon={showIcons ? <CalendarOutlined /> : undefined}
        />
      )}
      {updatedAt && (
        <TimestampDisplay
          date={updatedAt}
          label={t('updatedAt')}
          showLabel={true}
          icon={showIcons ? <EditOutlined /> : undefined}
        />
      )}
    </div>
  );
}

export default TimestampDisplay;
